import React, { useState } from 'react';
import { AlertCircle, FileText, Scale, Shield, ExternalLink, Download, ChevronDown, ChevronUp } from 'lucide-react';
import { Link } from 'react-router-dom';
import Icon from '../../components/AppIcon';


const LegalDisclaimer = () => {
  const [expandedSections, setExpandedSections] = React.useState({});

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev?.[sectionId]
    }));
  };

  const sections = [
    {
      id: 'general-info',
      title: '1. General Information & Website Purpose',
      icon: FileText,
      content: `This website (sailorsbail.com) is owned and operated by Sailors Bail Bonds. The information provided on this website is for general informational purposes only and should not be construed as legal advice or a substitute for consultation with a qualified attorney.

While we strive to provide accurate and up-to-date information about bail bond services, bail processes, and related legal matters, we make no warranties or representations about the accuracy, completeness, or timeliness of the information provided.`
    },
    {
      id: 'service-scope',
      title: '2. Scope of Bail Bond Services',
      icon: Scale,
      content: `Sailors Bail Bonds provides bail bond services in Sacramento, Stockton, Modesto, and surrounding California counties. Our services include:

• Emergency 24/7 bail bond assistance
• Consultation on bail processes and requirements
• Coordination with courts and detention facilities
• Payment plan arrangements
• Family support and guidance throughout the bail process

IMPORTANT LIMITATIONS:
- We are NOT attorneys and do NOT provide legal representation
- We do NOT guarantee specific case outcomes or court decisions
- We do NOT influence judicial proceedings or bail determinations
- We are NOT responsible for actions taken by courts, law enforcement, or other parties`
    },
    {
      id: 'no-legal-advice',
      title: '3. No Legal Advice or Representation',
      icon: AlertCircle,
      content: `CRITICAL DISCLAIMER: Sailors Bail Bonds and its agents are NOT attorneys and do NOT provide legal advice.

What We Do NOT Do:
• Provide legal opinions or interpretations of law
• Represent clients in court proceedings
• Advise on criminal defense strategies
• Interpret bail conditions or court orders
• Predict case outcomes or sentencing

What We Recommend:
We strongly recommend that anyone facing criminal charges or involved in legal proceedings consult with a qualified criminal defense attorney. Our role is limited to posting bail bonds and facilitating release from custody.

For legal representation, please contact:
• Your private attorney
• The Public Defender's Office
• A criminal defense attorney in your jurisdiction`
    },
    {
      id: 'liability-limits',title: '4. Limitations of Liability',
      icon: Shield,
      content: `Sailors Bail Bonds shall NOT be held liable for:

Court Outcomes & Decisions:
• Changes in bail amounts set by courts
• Denial of bail by judicial officers
• Revocation of bail for any reason
• Court scheduling or procedural delays
• Case outcomes, verdicts, or sentences

Collateral & Financial Matters:
• Market value fluctuations of collateral
• Inability to return to collateral due to defendant non-appearance
• Financial losses resulting from bond forfeiture
• Third-party financial institution policies or decisions

Detention Facility Operations:
• Release processing delays by jails or detention facilities
• Accuracy of inmate information provided by facilities
• Facility policies or procedures beyond our control
• Medical or safety issues within detention facilities

Third-Party Actions:
• Actions or inactions of defendants, co-signers, or family members
• Decisions made by attorneys, courts, or law enforcement
• Information accuracy from third-party sources
• Communication failures beyond our reasonable control`
    },
    {
      id: 'client-responsibilities',title: '5. Client Responsibilities & Acknowledgments',
      icon: FileText,
      content: `By engaging our bail bond services, clients acknowledge and agree to the following responsibilities:

Defendant Obligations:
✓ Appear at ALL scheduled court appearances without exception
✓ Comply with ALL bail conditions set by the court
✓ Maintain contact with Sailors Bail Bonds as required
✓ Report any address changes or travel plans immediately
✓ Avoid any criminal activity while released on bail
✓ Surrender immediately if bail is revoked

Co-Signer/Indemnitor Obligations:
✓ Ensure defendant compliance with all bail conditions
✓ Report defendant whereabouts if contact is lost
✓ Maintain communication with Sailors Bail Bonds
✓ Protect and maintain any collateral pledged
✓ Pay all fees and charges as agreed
✓ Accept financial responsibility for bond forfeiture if applicable

Understanding & Acknowledgment:
✓ I understand that posting bail does NOT guarantee case dismissal
✓ I understand that bail bonds are NON-REFUNDABLE service fees
✓ I understand collateral may be forfeited if defendant fails to appear
✓ I understand I am financially responsible for the full bond amount
✓ I have been advised to seek independent legal counsel
✓ I understand Sailors Bail Bonds does NOT provide legal advice`
    },
    {
      id: 'forfeiture-scenarios',title: '6. Bond Forfeiture & Financial Consequences',
      icon: AlertCircle,
      content: `CRITICAL INFORMATION: Bond forfeiture occurs when a defendant fails to appear in court as required.

Financial Consequences of Forfeiture:
• The full bond amount becomes immediately due and payable
• Collateral pledged may be seized and liquidated
• Additional fees and costs may be assessed
• Legal action may be taken to recover the full bond amount
• Credit reporting of unpaid obligations may occur
• Collection proceedings may be initiated

Forfeiture Scenarios:
1. Defendant fails to appear at any scheduled court date
2. Defendant violates any condition of bail release
3. Defendant commits new crimes while on bail
4. Defendant flees jurisdiction or goes into hiding
5. Defendant fails to surrender when ordered by court

Possible Remedies:
• Immediate notification to Sailors Bail Bonds if defendant is located
• Court motion to set aside forfeiture (time-limited)
• Apprehension and surrender of defendant within allowed timeframe
• Payment arrangements if forfeiture is sustained

Prevention:
→ Maintain constant communication with Sailors Bail Bonds
→ Ensure defendant understands all court appearance dates
→ Report any concerns about defendant compliance immediately
→ Keep defendant's contact information current`
    },
    {
      id: 'regulatory-compliance',
      title: '7. Regulatory Compliance & Licensing',
      icon: Scale,
      content: `Sailors Bail Bonds is a licensed bail bond agency operating under California law.

Licensing & Regulation:
• Licensed by the California Department of Insurance
• Subject to California Insurance Code bail bond provisions
• Compliant with all applicable federal and state regulations
• Operating under strict professional standards

Industry Standards:
• Member of professional bail bond associations
• Adherence to ethical business practices
• Compliance with TCPA (Telephone Consumer Protection Act)
• Privacy policy compliance with CCPA (California Consumer Privacy Act)

Regulatory Oversight:
For questions about our licensing or to file complaints:
• California Department of Insurance: www.insurance.ca.gov
• Consumer Complaint Hotline: 1-800-927-4357
• Professional Bail Agents of California: www.pbac.org

Our license information is available upon request and displayed at our physical office location.`
    },
    {
      id: 'website-disclaimer',
      title: '8. Website Content & Information Accuracy',
      icon: FileText,
      content: `Website Information Disclaimer:

General Information Only:
• All content is provided for general informational purposes
• Information should not be relied upon without independent verification
• Laws, procedures, and policies may change without notice
• Website content may not reflect the most current legal developments

No Warranty:
Sailors Bail Bonds makes NO warranties regarding:
• Accuracy or completeness of information
• Timeliness of updates or corrections
• Suitability for any particular purpose
• Error-free operation of the website

Third-Party Links:
• Links to third-party websites are provided for convenience only
• We do not endorse or control third-party content
• We are not responsible for third-party website accuracy or policies
• Use of third-party links is at your own risk

Changes to Content:
• We reserve the right to modify content without notice
• Information may be updated, removed, or replaced at any time
• Historical information may not reflect current practices or policies

Professional Advice Recommended:
For specific legal questions or case-specific guidance:
→ Consult with a licensed attorney in your jurisdiction
→ Contact court clerks for procedural questions
→ Verify all information with official sources`
    },
    {
      id: 'privacy-communication',
      title: '9. Privacy & Communication Consent',
      icon: Shield,
      content: `Communication Practices:

SMS/Text Messaging:
• By providing your phone number, you consent to SMS communications
• Standard message and data rates may apply
• You may opt out by replying STOP to any message
• Our registered phone number: (916) 299-9182

Email Communications:
• You may receive email updates about your case or services
• You can unsubscribe from marketing emails at any time
• Critical case-related emails will continue as necessary

Phone Calls:
• We may contact you by phone regarding your bail bond matter
• Calls may be recorded for quality assurance and training
• You consent to calls at the number(s) you provide

Privacy Protection:
• All client information is kept strictly confidential
• We comply with applicable privacy laws (CCPA, etc.)
• Information is only shared as legally required or with your consent
• See our full Privacy Policy for detailed information

Data Security:
• We implement reasonable security measures to protect your data
• No electronic transmission is 100% secure
• You are responsible for maintaining the security of your account credentials`
    },
    {
      id: 'professional-advice',
      title: '10. Requirement for Professional Consultation',
      icon: AlertCircle,
      content: `MANDATORY PROFESSIONAL ADVICE NOTICE:

This Website Does NOT Replace Professional Counsel

Legal Matters:
🔴 ALWAYS consult with a qualified criminal defense attorney for:
• Legal strategy and defense planning
• Interpretation of charges or potential sentences
• Court procedure and appearance requirements
• Plea negotiations or trial preparation
• Understanding your legal rights and options

Financial Matters:
🔴 CONSULT with financial advisors regarding:
• Collateral valuation and protection strategies
• Tax implications of bail bond transactions
• Long-term financial planning during legal proceedings
• Asset protection and risk management

Mental Health Support:
🔴 SEEK professional help for:
• Stress and anxiety related to legal proceedings
• Family counseling and support services
• Substance abuse treatment if applicable
• Mental health resources and crisis intervention

Our Role is Limited:
✓ We facilitate bail bond posting and release from custody
✓ We provide general information about bail processes
✓ We offer payment arrangements and customer service
✓ We coordinate with courts and detention facilities

✗ We do NOT provide legal, financial, tax, or medical advice
✗ We do NOT substitute for professional consultations
✗ We do NOT guarantee outcomes or results

Seek Professional Help:
→ Criminal Defense Attorney: For all legal matters
→ Financial Advisor: For financial planning and asset protection
→ Mental Health Professional: For emotional and psychological support
→ Substance Abuse Counselor: If addiction issues are involved`
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-900 to-blue-700 text-white py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <Scale className="w-16 h-16 mx-auto mb-6" />
          <h1 className="text-4xl md:text-5xl font-black mb-6" style={{ fontFamily: 'Merriweather, serif' }}>
            Legal Disclaimer
          </h1>
          <p className="text-xl text-blue-100 mb-8" style={{ fontFamily: 'Inter, sans-serif' }}>
            Important legal information about our services and limitations
          </p>
          <div className="bg-blue-800/50 rounded-2xl p-6 max-w-2xl mx-auto">
            <p className="text-lg text-white font-semibold">
              "This site offers general info; always seek professional advice for your situation."
            </p>
          </div>
        </div>
      </div>
      {/* Critical Notice */}
      <div className="max-w-4xl mx-auto px-4 -mt-10">
        <div className="bg-red-100 border-2 border-red-300 rounded-2xl p-8 shadow-xl">
          <div className="flex items-start">
            <AlertCircle className="w-8 h-8 text-red-600 mr-4 flex-shrink-0 mt-1" />
            <div>
              <h2 className="text-2xl font-bold text-red-900 mb-4">Critical Legal Notice</h2>
              <p className="text-red-800 mb-4 leading-relaxed">
                <strong>PLEASE READ CAREFULLY:</strong> The information on this page contains important legal disclaimers about Sailors Bail Bonds services, limitations, and client responsibilities. By using our services, you acknowledge that you have read, understood, and agree to these terms.
              </p>
              <p className="text-red-700 font-semibold">
                ⚠️ We strongly recommend consulting with a licensed attorney for legal advice specific to your situation.
              </p>
            </div>
          </div>
        </div>
      </div>
      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 py-16">
        {/* Introduction */}
        <div className="prose prose-lg max-w-none mb-16">
          <p className="text-xl text-gray-700 leading-relaxed" style={{ fontFamily: 'Inter, sans-serif' }}>
            We want you to understand your rights and our role in this process. Sailors Bail Bonds is committed to providing professional, ethical bail bond services while being transparent about the limitations of what we can and cannot do for our clients.
          </p>
        </div>

        {/* Expandable Sections */}
        <div className="space-y-6">
          {sections?.map((section) => {
            const Icon = section?.icon;
            const isExpanded = expandedSections?.[section?.id];
            
            return (
              <div 
                key={section?.id}
                className="bg-white rounded-2xl shadow-lg border-2 border-gray-200 overflow-hidden transition-all hover:shadow-xl"
              >
                <button
                  onClick={() => toggleSection(section?.id)}
                  className="w-full px-8 py-6 flex items-center justify-between text-left hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center flex-1">
                    <div className="bg-gradient-to-br from-blue-100 to-blue-200 rounded-xl p-4 mr-6">
                      <Icon className="w-6 h-6 text-blue-600" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900" style={{ fontFamily: 'Merriweather, serif' }}>
                      {section?.title}
                    </h3>
                  </div>
                  {isExpanded ? (
                    <ChevronUp className="w-6 h-6 text-gray-400" />
                  ) : (
                    <ChevronDown className="w-6 h-6 text-gray-400" />
                  )}
                </button>
                {isExpanded && (
                  <div className="px-8 py-6 bg-gray-50 border-t-2 border-gray-200">
                    <div className="prose prose-blue max-w-none">
                      <pre className="whitespace-pre-wrap font-sans text-base text-gray-700 leading-relaxed">
                        {section?.content}
                      </pre>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Download Section */}
        <div className="mt-16 bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-8 border-2 border-blue-200">
          <div className="flex flex-col md:flex-row items-center justify-between">
            <div className="mb-6 md:mb-0 md:mr-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Merriweather, serif' }}>
                Download Full Legal Disclaimer
              </h3>
              <p className="text-gray-700">
                Save a complete copy of our legal disclaimer for your records
              </p>
            </div>
            <button
              onClick={() => window.print()}
              className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white px-8 py-4 rounded-xl font-bold flex items-center transition-all transform hover:scale-105 shadow-lg whitespace-nowrap"
            >
              <Download className="w-5 h-5 mr-2" />
              Download PDF
            </button>
          </div>
        </div>

        {/* Contact Section */}
        <div className="mt-16 bg-white rounded-2xl shadow-xl p-8 border-2 border-gray-200">
          <div className="text-center">
            <h3 className="text-3xl font-bold text-gray-900 mb-6" style={{ fontFamily: 'Merriweather, serif' }}>
              Questions About Our Disclaimer?
            </h3>
            <p className="text-xl text-gray-700 mb-8 max-w-2xl mx-auto">
              If you have questions about any of the terms in this disclaimer, please contact us. We're here to help clarify our services and limitations.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="tel:9162563468"
                className="bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white px-8 py-4 rounded-xl font-bold text-lg transition-all transform hover:scale-105 shadow-lg flex items-center justify-center"
              >
                <FileText className="w-5 h-5 mr-2" />
                Call Us: (916) 256-3468
              </a>
              <Link
                to="/contact-us"
                className="bg-white border-2 border-blue-600 text-blue-600 hover:bg-blue-50 px-8 py-4 rounded-xl font-bold text-lg transition-all flex items-center justify-center"
              >
                Contact Us Online
                <ExternalLink className="w-5 h-5 ml-2" />
              </Link>
            </div>
          </div>
        </div>

        {/* Footer Links */}
        <div className="mt-16 text-center">
          <p className="text-gray-600 mb-6">Related Legal Documents:</p>
          <div className="flex flex-wrap justify-center gap-6">
            <Link 
              to="/privacy-policy"
              className="text-blue-600 hover:text-blue-800 font-semibold hover:underline flex items-center"
            >
              Privacy Policy
              <ExternalLink className="w-4 h-4 ml-1" />
            </Link>
            <Link 
              to="/terms-and-conditions"
              className="text-blue-600 hover:text-blue-800 font-semibold hover:underline flex items-center"
            >
              Terms of Service
              <ExternalLink className="w-4 h-4 ml-1" />
            </Link>
            <Link 
              to="/homepage-redesign"
              className="text-blue-600 hover:text-blue-800 font-semibold hover:underline flex items-center"
            >
              Back to Home
              <ExternalLink className="w-4 h-4 ml-1" />
            </Link>
          </div>
        </div>

        {/* Last Updated */}
        <div className="mt-12 text-center text-gray-500 text-sm">
          <p>Last Updated: {new Date()?.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
          <p className="mt-2">Sailors Bail Bonds | 901 H Street, Suite 103, Sacramento, CA 95814</p>
        </div>
      </div>
    </div>
  );
};

export default LegalDisclaimer;